// pdfProcessor.js
import { processGroceryLeaflet } from '../services/azureVisionService.js';
import { processPdfWithBackend } from '../services/backendService.js';
import { FEATURES, MOCK_DATA } from '../config.js';

/**
 * Process a PDF file using the appropriate method (backend or client-side)
 * @param {ArrayBuffer} pdfBuffer - The PDF file as an ArrayBuffer
 * @param {File} originalFile - The original file object (needed for backend processing)
 * @returns {Promise<Array>} - Array of extracted grocery items
 */
export async function extractPdfContent(pdfBuffer, originalFile) {
  try {
    // Determine which processing method to use
    if (FEATURES.USE_BACKEND && !MOCK_DATA.USE_MOCK_DATA) {
      console.log('Using backend for PDF processing');
      
      // Process with backend
      return await processPdfWithBackend(originalFile);
    } else {
      console.log('Using client-side processing');
      
      // Use the global pdfjsLib that's set in the HTML
      const pdfjsLib = window.pdfjsLib;
      
      // Load PDF document
      const loadingTask = pdfjsLib.getDocument({ data: pdfBuffer });
      const pdfDocument = await loadingTask.promise;
      
      const totalPages = pdfDocument.numPages;
      console.log(`PDF has ${totalPages} pages`);
      
      // Extract images from each page for Azure AI processing
      const pageImages = [];
      
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdfDocument.getPage(pageNum);
        
        // Create a viewport for rendering
        const viewport = page.getViewport({ scale: 1.5 }); // Higher scale for better image quality
        const { width, height } = viewport;
        
        // Create a canvas for rendering the page
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // Render the page to canvas
        await page.render({ canvasContext: ctx, viewport }).promise;
        
        // Get the rendered page as an image
        const pageImageUrl = canvas.toDataURL('image/jpeg', 0.9); // Use JPEG with high quality
        pageImages.push(pageImageUrl);
      }
      
      // Process the pages with Azure AI services
      if (pageImages.length > 0) {
        try {
          const extractedItems = await processGroceryLeaflet(pdfBuffer, pageImages);
          
          // If items were successfully extracted, ensure numeric values and return them
          if (extractedItems && extractedItems.length > 0) {
            // Ensure all numeric values are correctly formatted
            const processedItems = extractedItems.map(item => ({
              ...item,
              discountPrice: typeof item.discountPrice === 'number' ? item.discountPrice : parseFloat(item.discountPrice) || 0,
              originalPrice: typeof item.originalPrice === 'number' ? item.originalPrice : parseFloat(item.originalPrice) || 0,
              discount: typeof item.discount === 'number' ? item.discount : parseFloat(item.discount) || 0
            }));
            
            return processedItems;
          }
        } catch (error) {
          console.error('Error processing with Azure AI:', error);
          // Continue to fallback method
        }
      }
      
      // Fallback: If Azure processing failed or returned no items, use the traditional extraction
      return extractItemsFromPdfFallback(pdfDocument);
    }
  } catch (error) {
    console.error('Error processing PDF:', error);
    // Fallback to sample data if processing fails
    return generateSampleItems();
  }
}

/**
 * Fallback method to extract items from PDF without Azure services
 * @param {PDFDocument} pdfDocument - The PDF document
 * @returns {Promise<Array>} - Array of extracted items
 */
async function extractItemsFromPdfFallback(pdfDocument) {
  try {
    const totalPages = pdfDocument.numPages;
    const extractedItems = [];
    
    // Process each page
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      const page = await pdfDocument.getPage(pageNum);
      
      // Extract text content
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      
      // Extract food items from text
      const textItems = extractFoodItemDetailsFromText(pageText);
      extractedItems.push(...textItems);
    }
    
    // If no items were found, generate some sample items
    if (extractedItems.length === 0) {
      return generateSampleItems();
    }
    
    // Return unique items (removing duplicates)
    const uniqueItems = removeDuplicates(extractedItems);
    
    // Ensure all numeric values are correctly formatted
    return uniqueItems.map(item => ({
      ...item,
      discountPrice: typeof item.discountPrice === 'number' ? item.discountPrice : parseFloat(item.discountPrice) || 0,
      originalPrice: typeof item.originalPrice === 'number' ? item.originalPrice : parseFloat(item.originalPrice) || 0,
      discount: typeof item.discount === 'number' ? item.discount : parseFloat(item.discount) || 0
    }));
  } catch (error) {
    console.error('Error in fallback PDF processing:', error);
    return generateSampleItems();
  }
}

/**
 * Generate sample items for demonstration
 */
function generateSampleItems() {
  return [
    { 
      id: 1, 
      name: 'Laxfilé hel sida', 
      category: 'seafood', 
      originalPrice: 199.00, 
      discountPrice: 149.00, 
      unit: 'kg', 
      image: 'images/placeholder.jpg', 
      discount: 25,
      description: 'PDF-extraherad produkt (exempeldata)' 
    },
    { 
      id: 2, 
      name: 'Kycklingbröstfilé', 
      category: 'meat', 
      originalPrice: 91.90, 
      discountPrice: 69.90, 
      unit: 'kg', 
      image: 'images/placeholder.jpg', 
      discount: 23,
      description: 'PDF-extraherad produkt (exempeldata)' 
    },
    { 
      id: 3, 
      name: 'Kvisttomater', 
      category: 'vegetables', 
      originalPrice: 39.90, 
      discountPrice: 24.90, 
      unit: 'kg', 
      image: 'images/placeholder.jpg', 
      discount: 37,
      description: 'PDF-extraherad produkt (exempeldata)' 
    },
    { 
      id: 4, 
      name: 'OLW snacks', 
      category: 'snacks', 
      originalPrice: 83.70, 
      discountPrice: 59.00, 
      unit: '3-pack', 
      image: 'images/placeholder.jpg', 
      discount: 30,
      description: 'PDF-extraherad produkt (exempeldata)' 
    },
    { 
      id: 6, 
      name: 'Gul lök', 
      category: 'vegetables', 
      originalPrice: 12.90, 
      discountPrice: 9.90, 
      unit: 'kg', 
      image: 'images/placeholder.jpg', 
      discount: 23,
      description: 'PDF-extraherad produkt (exempeldata)' 
    },
    { 
      id: 7, 
      name: 'Röda äpplen', 
      category: 'fruit', 
      originalPrice: 45.00, 
      discountPrice: 39.90, 
      unit: '2.5kg', 
      image: 'images/placeholder.jpg', 
      discount: 11,
      description: 'PDF-extraherad produkt (exempeldata)' 
    },
  ].map(item => ({
    ...item,
    // Ensure all values are numeric
    discountPrice: parseFloat(item.discountPrice) || 0,
    originalPrice: parseFloat(item.originalPrice) || 0,
    discount: parseFloat(item.discount) || 0
  }));
}

/**
 * Extract food items from text
 */
function extractFoodItemDetailsFromText(text, imageUrl = null) {
  const items = [];
  const lines = text.split('\n');
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Only process lines that look like they contain product info
    if (line.length > 10) {
      const pricePattern = /(\d+)[.,](\d+)\s*(?:kr|:-)/g;
      const discountPattern = /-(\d+)%/g;
      const productNamePattern = /([A-ZÅÄÖ][a-zåäö]+(?:\s+[a-zåäöA-ZÅÄÖ]+){1,4})/g;
      
      const priceMatch = line.match(pricePattern);
      const discountMatch = line.match(discountPattern);
      const nameMatch = line.match(productNamePattern);
      
      if (nameMatch && priceMatch) {
        const name = nameMatch[0];
        const price = parseFloat(priceMatch[0].replace(/[^0-9,.]/g, '').replace(',', '.'));
        let discount = 0;
        
        if (discountMatch) {
          discount = parseInt(discountMatch[1]);
        }
        
        // Skip items that don't seem to be food products
        if (isLikelyFoodProduct(name)) {
          // Determine category based on product name
          const category = determineCategory(name);
          
          // Convert price to a proper number
          const priceValue = parseFloat(price) || 0;
          // Calculate original price or use the same price if no discount
          const originalPriceValue = discount ? (priceValue / (1 - discount/100)) : priceValue;
          
          items.push({
            id: Date.now() + Math.floor(Math.random() * 1000) + i, // Generate a unique ID
            name,
            category,
            originalPrice: originalPriceValue,
            discountPrice: priceValue,
            unit: determineUnit(name, line),
            image: imageUrl || 'images/placeholder.jpg',
            discount,
            description: `Extracted from weekly flyer`
          });
        }
      }
    }
  }
  
  return items;
}

/**
 * Helper function to determine if a product name is likely a food product
 */
function isLikelyFoodProduct(name) {
  const nonFoodKeywords = ['gryta', 'tvål', 'diskmedel', 'handduk', 'kudde', 'möbel', 'duk'];
  const foodKeywords = [
    'äpple', 'banan', 'kött', 'korv', 'mjölk', 'ost', 'pasta', 
    'potatis', 'lax', 'kyckling', 'fläsk', 'nöt', 'tomat'
  ];
  
  // Check if it contains non-food keywords
  if (nonFoodKeywords.some(keyword => name.toLowerCase().includes(keyword))) {
    return false;
  }
  
  // Higher confidence if it contains food keywords
  if (foodKeywords.some(keyword => name.toLowerCase().includes(keyword))) {
    return true;
  }
  
  // Otherwise, make a best guess
  return !name.includes('kr') && name.length > 3 && name.length < 40;
}

/**
 * Helper function to determine category based on product name
 */
function determineCategory(name) {
  const name_lower = name.toLowerCase();
  
  if (name_lower.includes('lax') || name_lower.includes('torsk') || name_lower.includes('räk')) {
    return 'seafood';
  } else if (name_lower.includes('kyckling') || name_lower.includes('nöt') || name_lower.includes('fläsk') || name_lower.includes('karré')) {
    return 'meat';
  } else if (name_lower.includes('tomat') || name_lower.includes('lök') || name_lower.includes('paprika') || name_lower.includes('potatis')) {
    return 'vegetables';
  } else if (name_lower.includes('äpple') || name_lower.includes('banan') || name_lower.includes('päron') || name_lower.includes('frukt')) {
    return 'fruit';
  } else if (name_lower.includes('ost') || name_lower.includes('mjölk') || name_lower.includes('yoghurt')) {
    return 'dairy';
  } else if (name_lower.includes('snacks') || name_lower.includes('chips') || name_lower.includes('godis')) {
    return 'snacks';
  } else if (name_lower.includes('pasta') || name_lower.includes('ris') || name_lower.includes('mjöl')) {
    return 'pantry';
  } else {
    return 'other';
  }
}

/**
 * Helper function to determine unit based on product name and context
 */
function determineUnit(name, context) {
  const name_lower = name.toLowerCase();
  const context_lower = context.toLowerCase();
  
  if (context_lower.includes('kg')) {
    return 'kg';
  } else if (context_lower.includes('g ') || context_lower.includes('gram')) {
    return 'g';
  } else if (context_lower.includes('l ') || context_lower.includes('liter')) {
    return 'l';
  } else if (context_lower.includes('st ') || context_lower.includes('styck')) {
    return 'st';
  } else if (context_lower.includes('pack')) {
    return 'pack';
  } else {
    // Fallback to finding the unit in the name
    if (name_lower.includes('kg')) {
      return 'kg';
    } else if (name_lower.includes('g ')) {
      return 'g';
    } else if (name_lower.includes('l ') || name_lower.includes('liter')) {
      return 'l';
    } else if (name_lower.includes('st ') || name_lower.includes('styck')) {
      return 'st';
    } else {
      return 'st'; // Default unit
    }
  }
}

/**
 * Remove duplicate items based on name similarity
 */
function removeDuplicates(items) {
  const uniqueItems = [];
  const seenNames = new Set();
  
  for (const item of items) {
    // Normalize the name for comparison
    const normalizedName = item.name.toLowerCase().trim();
    
    // Check if we've seen a similar name
    let isDuplicate = false;
    for (const seenName of seenNames) {
      if (normalizedName.includes(seenName) || seenName.includes(normalizedName)) {
        isDuplicate = true;
        break;
      }
    }
    
    if (!isDuplicate) {
      seenNames.add(normalizedName);
      uniqueItems.push(item);
    }
  }
  
  return uniqueItems;
}

// Helper function (no longer needed)
export async function releaseOCRWorker() {
  return true;
}
